import { Box, Button, Container, FloatingIndicator, Group, Scroller } from "@mantine/core";
import { useMemo, useState, type FC } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { LinkPermissionMode, type ILayoutLink } from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import styles from "./NavMenu.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import hasEveryPermission from "src/utilities/hasEveryPermission";
import hasAnyPermission from "src/utilities/hasAnyPermission";

interface INavMenuProps {
    menuItems?: ILayoutLink[];
}

interface INavMenuInternalProps {
    menuItems: ILayoutLink[];
}

const NavMenuInternal: FC<INavMenuInternalProps> = ({ menuItems }) => {
    const permissions = useCurrentUserPermissions();
    const location = useLocation();

    const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
    const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLAnchorElement | null>>({});

    const navigation = useMemo(() => {
        const setControlRef = (href: string) => (node: HTMLAnchorElement) => {
            setControlsRefs((current) => {
                const newRefs = { ...current };
                newRefs[href] = node;
                return newRefs;
            });
        };

        const allowedMenuItems = menuItems.filter((menuItem) => {
            const hasPermission =
                menuItem.permissionMode === LinkPermissionMode.All
                    ? hasEveryPermission(permissions, menuItem.permissions, undefined)
                    : hasAnyPermission(permissions, menuItem.permissions, undefined);

            return hasPermission;
        });

        const items: React.ReactNode[] = [];

        for (const menuItem of allowedMenuItems) {
            if (menuItem.actionFn) {
                items.push(
                    <Button
                        key={menuItem.href}
                        radius={0}
                        size="md"
                        ref={setControlRef(menuItem.href) as any}
                        onClick={menuItem.actionFn}
                        leftSection={
                            menuItem.label && menuItem.icon ? (
                                <FontAwesomeIcon icon={menuItem.icon} />
                            ) : undefined
                        }
                        style={menuItem.style}
                        className={menuItem.className}
                    >
                        {menuItem.label ? (
                            menuItem.label
                        ) : menuItem.icon ? (
                            <FontAwesomeIcon icon={menuItem.icon} />
                        ) : null}
                    </Button>,
                );

                continue;
            }

            items.push(
                <Button
                    key={menuItem.href}
                    radius={0}
                    size="md"
                    ref={setControlRef(menuItem.href)}
                    component={RouterLink}
                    to={menuItem.href}
                    target={menuItem.newTab ? "_blank" : undefined}
                    leftSection={
                        menuItem.label && menuItem.icon ? (
                            <FontAwesomeIcon icon={menuItem.icon} />
                        ) : undefined
                    }
                    style={menuItem.style}
                    className={menuItem.className}
                >
                    {menuItem.label ? (
                        menuItem.label
                    ) : menuItem.icon ? (
                        <FontAwesomeIcon icon={menuItem.icon} />
                    ) : null}
                </Button>,
            );
        }

        return {
            items,
            allowedMenuItems,
        };
    }, [menuItems, permissions, setControlsRefs]);

    const selectedHref = useMemo(() => {
        return navigation.allowedMenuItems.find((menuItem) =>
            location.pathname.startsWith(menuItem.href),
        )?.href;
    }, [navigation, location.pathname]);

    return (
        <Box bg="primary">
            <Container size="xl">
                <Scroller
                    draggable
                    edgeGradientColor="primary"
                    styles={{
                        content: {
                            minWidth: "100%",
                        },
                    }}
                >
                    <Group gap={0} ref={setRootRef} pos="relative" wrap="nowrap" w="100%">
                        {navigation.items}
                        <FloatingIndicator
                            target={controlsRefs[selectedHref]}
                            parent={rootRef}
                            className={styles.indicator}
                        />
                    </Group>
                </Scroller>
            </Container>
        </Box>
    );
};

const NavMenu: FC<INavMenuProps> = ({ menuItems }) => {
    if (!menuItems || menuItems.length === 0) {
        return null;
    }

    return <NavMenuInternal menuItems={menuItems} />;
};

export default NavMenu;
